import React, { useState, useEffect } from 'react';

interface AnalyticsMetric {
  id: string;
  name: string;
  value: number;
  previousValue: number;
  change: number;
  changePercent: number;
  trend: 'up' | 'down' | 'stable';
  format: 'number' | 'currency' | 'percentage';
  timeframe: string;
}

interface ChartDataPoint {
  date: string;
  value: number;
  label?: string;
}

interface AnalyticsData {
  overview: {
    totalRevenue: AnalyticsMetric;
    activeVendors: AnalyticsMetric;
    totalReviews: AnalyticsMetric;
    platformGrowth: AnalyticsMetric;
    trustedTesters: AnalyticsMetric;
    averageRating: AnalyticsMetric;
  };
  charts: {
    revenueChart: ChartDataPoint[];
    vendorSignups: ChartDataPoint[];
    reviewActivity: ChartDataPoint[];
    userEngagement: ChartDataPoint[];
  };
  topPerformers: {
    vendors: Array<{
      id: string;
      name: string;
      revenue: number;
      reviews: number;
      rating: number;
      growth: number;
    }>;
    products: Array<{
      id: string;
      name: string;
      vendor: string;
      views: number;
      orders: number;
      rating: number;
    }>;
    testers: Array<{
      id: string;
      name: string;
      reviews: number;
      rating: number;
      specialties: string[];
    }>;
  };
  insights: Array<{
    id: string;
    type: 'opportunity' | 'warning' | 'success' | 'info';
    title: string;
    description: string;
    actionable: boolean;
    impact: 'high' | 'medium' | 'low';
  }>;
}

interface AdvancedAnalyticsDashboardProps {
  userRole: 'admin' | 'staff' | 'vendor';
  timeframe: '7d' | '30d' | '90d' | '1y';
}

export default function AdvancedAnalyticsDashboard({ userRole, timeframe = '30d' }: AdvancedAnalyticsDashboardProps) {
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null);
  const [selectedTab, setSelectedTab] = useState<'overview' | 'vendors' | 'users' | 'revenue' | 'insights'>('overview');
  const [loading, setLoading] = useState(true);
  const [selectedMetric, setSelectedMetric] = useState<string>('revenue');

  useEffect(() => {
    const fetchAnalytics = async () => {
      setLoading(true);
      
      setTimeout(() => {
        const mockData: AnalyticsData = {
          overview: {
            totalRevenue: {
              id: 'revenue',
              name: 'Total Revenue',
              value: 127450,
              previousValue: 98230,
              change: 29220,
              changePercent: 29.7,
              trend: 'up',
              format: 'currency',
              timeframe: '30 days'
            },
            activeVendors: {
              id: 'vendors',
              name: 'Active Vendors',
              value: 156,
              previousValue: 142,
              change: 14,
              changePercent: 9.9,
              trend: 'up',
              format: 'number',
              timeframe: '30 days'
            },
            totalReviews: {
              id: 'reviews',
              name: 'Total Reviews',
              value: 2847,
              previousValue: 2156,
              change: 691,
              changePercent: 32.0,
              trend: 'up',
              format: 'number',
              timeframe: '30 days'
            },
            platformGrowth: {
              id: 'growth',
              name: 'Platform Growth',
              value: 23.5,
              previousValue: 18.2,
              change: 5.3,
              changePercent: 29.1,
              trend: 'up',
              format: 'percentage',
              timeframe: '30 days'
            },
            trustedTesters: {
              id: 'testers',
              name: 'Trusted Testers',
              value: 89,
              previousValue: 76,
              change: 13,
              changePercent: 17.1,
              trend: 'up',
              format: 'number',
              timeframe: '30 days'
            },
            averageRating: {
              id: 'rating',
              name: 'Average Rating',
              value: 4.6,
              previousValue: 4.4,
              change: 0.2,
              changePercent: 4.5,
              trend: 'up',
              format: 'number',
              timeframe: '30 days'
            }
          },
          charts: {
            revenueChart: [
              { date: '2024-01-01', value: 3200 },
              { date: '2024-01-05', value: 3450 },
              { date: '2024-01-10', value: 3890 },
              { date: '2024-01-15', value: 4120 },
              { date: '2024-01-20', value: 4680 },
              { date: '2024-01-25', value: 5100 },
              { date: '2024-01-30', value: 5340 }
            ],
            vendorSignups: [
              { date: '2024-01-01', value: 142 },
              { date: '2024-01-05', value: 145 },
              { date: '2024-01-10', value: 148 },
              { date: '2024-01-15', value: 151 },
              { date: '2024-01-20', value: 153 },
              { date: '2024-01-25', value: 155 },
              { date: '2024-01-30', value: 156 }
            ],
            reviewActivity: [
              { date: '2024-01-01', value: 45 },
              { date: '2024-01-05', value: 52 },
              { date: '2024-01-10', value: 68 },
              { date: '2024-01-15', value: 74 },
              { date: '2024-01-20', value: 89 },
              { date: '2024-01-25', value: 95 },
              { date: '2024-01-30', value: 103 }
            ],
            userEngagement: [
              { date: '2024-01-01', value: 1250 },
              { date: '2024-01-05', value: 1340 },
              { date: '2024-01-10', value: 1420 },
              { date: '2024-01-15', value: 1580 },
              { date: '2024-01-20', value: 1650 },
              { date: '2024-01-25', value: 1780 },
              { date: '2024-01-30', value: 1890 }
            ]
          },
          topPerformers: {
            vendors: [
              { id: '1', name: 'Premium Kratom Co', revenue: 28450, reviews: 187, rating: 4.8, growth: 34.2 },
              { id: '2', name: 'Natural Leaf Supply', revenue: 22100, reviews: 156, rating: 4.7, growth: 28.1 },
              { id: '3', name: 'Elite Botanicals', revenue: 19800, reviews: 142, rating: 4.6, growth: 22.5 },
              { id: '4', name: 'Pure Kratom Plus', revenue: 17600, reviews: 134, rating: 4.5, growth: 19.8 },
              { id: '5', name: 'Green Leaf Kratom', revenue: 15200, reviews: 128, rating: 4.4, growth: 16.3 }
            ],
            products: [
              { id: '1', name: 'Red Maeng Da Premium', vendor: 'Premium Kratom Co', views: 3420, orders: 234, rating: 4.9 },
              { id: '2', name: 'Green Thai Kratom', vendor: 'Natural Leaf Supply', views: 2890, orders: 198, rating: 4.8 },
              { id: '3', name: 'White Borneo Extract', vendor: 'Elite Botanicals', views: 2650, orders: 167, rating: 4.7 },
              { id: '4', name: 'Red Bali Superior', vendor: 'Pure Kratom Plus', views: 2340, orders: 156, rating: 4.6 },
              { id: '5', name: 'Gold Maeng Da', vendor: 'Green Leaf Kratom', views: 2120, orders: 142, rating: 4.5 }
            ],
            testers: [
              { id: '1', name: 'Sarah M.', reviews: 47, rating: 4.9, specialties: ['Red Strains', 'Pain Management'] },
              { id: '2', name: 'Michael R.', reviews: 42, rating: 4.8, specialties: ['Energy Strains', 'White Varieties'] },
              { id: '3', name: 'Jennifer L.', reviews: 38, rating: 4.7, specialties: ['Sleep Aid', 'Anxiety Relief'] },
              { id: '4', name: 'David K.', reviews: 35, rating: 4.9, specialties: ['Quality Control', 'All Varieties'] },
              { id: '5', name: 'Lisa P.', reviews: 31, rating: 4.6, specialties: ['Green Strains', 'Mood Enhancement'] }
            ]
          },
          insights: [
            {
              id: '1',
              type: 'opportunity',
              title: 'Red Strain Demand Surge',
              description: 'Red strain searches increased 45% this month. Consider promoting red strain vendors or recruiting specialists.',
              actionable: true,
              impact: 'high'
            },
            {
              id: '2',
              type: 'success',
              title: 'Trusted Tester Program Growth',
              description: 'Trusted tester applications up 67% with 94% approval rate. Quality reviews improving platform trust.',
              actionable: false,
              impact: 'high'
            },
            {
              id: '3',
              type: 'warning',
              title: 'Vendor Response Time Declining',
              description: 'Average vendor response time increased to 8.2 hours. May impact customer satisfaction.',
              actionable: true,
              impact: 'medium'
            },
            {
              id: '4',
              type: 'info',
              title: 'Mobile Usage Growing',
              description: 'Mobile traffic now represents 68% of total visits, up from 54% last quarter.',
              actionable: true,
              impact: 'medium'
            }
          ]
        };

        setAnalyticsData(mockData);
        setLoading(false);
      }, 1500);
    };

    fetchAnalytics();
  }, [timeframe]);

  const formatValue = (value: number, format: string) => {
    switch (format) {
      case 'currency':
        return `$${value.toLocaleString()}`;
      case 'percentage':
        return `${value.toFixed(1)}%`;
      case 'number':
        return value.toLocaleString();
      default:
        return value.toString();
    }
  };

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'up': return '📈';
      case 'down': return '📉';
      case 'stable': return '➡️';
      default: return '📊';
    }
  };

  const getTrendColor = (trend: string) => {
    switch (trend) {
      case 'up': return '#10b981';
      case 'down': return '#ef4444';
      case 'stable': return '#6b7280';
      default: return '#6b7280';
    }
  };

  const getInsightColor = (type: string) => {
    switch (type) {
      case 'opportunity': return '#3b82f6';
      case 'warning': return '#f59e0b';
      case 'success': return '#10b981';
      case 'info': return '#8b5cf6';
      default: return '#6b7280';
    }
  };

  const getInsightIcon = (type: string) => {
    switch (type) {
      case 'opportunity': return '💡';
      case 'warning': return '⚠️';
      case 'success': return '✅';
      case 'info': return 'ℹ️';
      default: return '📋';
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <div style={{ display: 'inline-block', width: '32px', height: '32px', border: '3px solid #f3f3f3', borderTop: '3px solid #3498db', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
        <p>Loading analytics data...</p>
      </div>
    );
  }

  if (!analyticsData) {
    return <div style={{ padding: '2rem', textAlign: 'center' }}>Analytics data not available</div>;
  }

  return (
    <div style={{ padding: '1.5rem', maxWidth: '1400px', margin: '0 auto', backgroundColor: '#1a202c', minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{ fontSize: '2.25rem', fontWeight: 'bold', marginBottom: '0.5rem', color: '#e2e8f0' }}>
          Advanced Analytics Dashboard
        </h1>
        <p style={{ color: '#a0aec0', fontSize: '1.125rem' }}>
          Comprehensive insights and performance metrics for UnbiasedKratom platform
        </p>
      </div>

      {/* Key Metrics Overview */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
        gap: '1.5rem', 
        marginBottom: '3rem' 
      }}>
        {Object.values(analyticsData.overview).map(metric => (
          <div key={metric.id} style={{ 
            backgroundColor: '#2d3748', 
            padding: '2rem', 
            borderRadius: '0.75rem', 
            boxShadow: '0 4px 6px rgba(0,0,0,0.3)',
            border: '1px solid #4a5568',
            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
            cursor: 'pointer'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 8px 12px rgba(0,0,0,0.1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.05)';
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
              <div>
                <h3 style={{ fontSize: '0.875rem', fontWeight: '600', color: '#6b7280', margin: '0 0 0.5rem 0', textTransform: 'uppercase' }}>
                  {metric.name}
                </h3>
                <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#e2e8f0', lineHeight: '1' }}>
                  {formatValue(metric.value, metric.format)}
                </div>
              </div>
              <div style={{ fontSize: '1.5rem' }}>
                {getTrendIcon(metric.trend)}
              </div>
            </div>
            
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span style={{ 
                  fontSize: '0.875rem', 
                  fontWeight: '600',
                  color: getTrendColor(metric.trend)
                }}>
                  {metric.changePercent > 0 ? '+' : ''}{metric.changePercent.toFixed(1)}%
                </span>
                <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                  vs {metric.timeframe}
                </span>
              </div>
              <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                {formatValue(metric.change, metric.format)} change
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Tab Navigation */}
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ borderBottom: '2px solid #e5e7eb' }}>
          <div style={{ display: 'flex', gap: '2rem', overflowX: 'auto' }}>
            {(['overview', 'vendors', 'users', 'revenue', 'insights'] as const).map(tab => (
              <button
                key={tab}
                onClick={() => setSelectedTab(tab)}
                style={{
                  padding: '1rem 0',
                  backgroundColor: 'transparent',
                  border: 'none',
                  borderBottom: selectedTab === tab ? '3px solid #3b82f6' : '3px solid transparent',
                  color: selectedTab === tab ? '#3b82f6' : '#6b7280',
                  fontSize: '1rem',
                  fontWeight: selectedTab === tab ? '600' : '500',
                  cursor: 'pointer',
                  textTransform: 'capitalize',
                  whiteSpace: 'nowrap',
                  transition: 'all 0.2s ease'
                }}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      {selectedTab === 'overview' && (
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '2rem' }}>
          {/* Revenue Chart */}
          <div style={{ backgroundColor: '#2d3748', padding: '2rem', borderRadius: '0.75rem', boxShadow: '0 4px 6px rgba(0,0,0,0.3)' }}>
            <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1.5rem', color: '#e2e8f0' }}>
              Revenue Trend (Last 30 Days)
            </h3>
            <div style={{ height: '300px', display: 'flex', alignItems: 'end', gap: '1rem', padding: '1rem 0' }}>
              {analyticsData.charts.revenueChart.map((point, index) => (
                <div key={index} style={{ flex: '1', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <div style={{ 
                    width: '100%', 
                    backgroundColor: '#3b82f6',
                    borderRadius: '4px 4px 0 0',
                    height: `${(point.value / Math.max(...analyticsData.charts.revenueChart.map(p => p.value))) * 200}px`,
                    minHeight: '4px',
                    transition: 'height 0.3s ease'
                  }}></div>
                  <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.5rem', textAlign: 'center' }}>
                    {new Date(point.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#e2e8f0', fontWeight: '500' }}>
                    ${point.value.toLocaleString()}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Top Insights */}
          <div style={{ backgroundColor: '#2d3748', padding: '2rem', borderRadius: '0.75rem', boxShadow: '0 4px 6px rgba(0,0,0,0.3)' }}>
            <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1.5rem', color: '#e2e8f0' }}>
              Key Insights
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {analyticsData.insights.slice(0, 3).map(insight => (
                <div key={insight.id} style={{ 
                  padding: '1rem', 
                  backgroundColor: getInsightColor(insight.type) + '10',
                  borderRadius: '0.5rem',
                  borderLeft: `4px solid ${getInsightColor(insight.type)}`
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    <span style={{ fontSize: '1rem' }}>{getInsightIcon(insight.type)}</span>
                    <h4 style={{ fontSize: '0.875rem', fontWeight: '600', color: '#e2e8f0', margin: '0' }}>
                      {insight.title}
                    </h4>
                    <span style={{ 
                      fontSize: '0.75rem', 
                      padding: '0.125rem 0.375rem',
                      backgroundColor: getInsightColor(insight.type) + '20',
                      color: getInsightColor(insight.type),
                      borderRadius: '0.25rem',
                      textTransform: 'uppercase',
                      fontWeight: '600'
                    }}>
                      {insight.impact}
                    </span>
                  </div>
                  <p style={{ fontSize: '0.875rem', color: '#a0aec0', margin: '0', lineHeight: '1.4' }}>
                    {insight.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {selectedTab === 'vendors' && (
        <div style={{ backgroundColor: '#2d3748', padding: '2rem', borderRadius: '0.75rem', boxShadow: '0 4px 6px rgba(0,0,0,0.3)' }}>
          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1.5rem', color: '#e2e8f0' }}>
            Top Performing Vendors
          </h3>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                  <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: '#a0aec0' }}>Vendor</th>
                  <th style={{ padding: '0.75rem', textAlign: 'right', fontSize: '0.875rem', fontWeight: '600', color: '#a0aec0' }}>Revenue</th>
                  <th style={{ padding: '0.75rem', textAlign: 'right', fontSize: '0.875rem', fontWeight: '600', color: '#a0aec0' }}>Reviews</th>
                  <th style={{ padding: '0.75rem', textAlign: 'right', fontSize: '0.875rem', fontWeight: '600', color: '#a0aec0' }}>Rating</th>
                  <th style={{ padding: '0.75rem', textAlign: 'right', fontSize: '0.875rem', fontWeight: '600', color: '#a0aec0' }}>Growth</th>
                </tr>
              </thead>
              <tbody>
                {analyticsData.topPerformers.vendors.map((vendor, index) => (
                  <tr key={vendor.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '1rem 0.75rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <div style={{ 
                          width: '32px', 
                          height: '32px', 
                          borderRadius: '50%', 
                          backgroundColor: '#3b82f6',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontSize: '0.875rem',
                          fontWeight: 'bold'
                        }}>
                          #{index + 1}
                        </div>
                        <div>
                          <div style={{ fontSize: '0.875rem', fontWeight: '600', color: '#e2e8f0' }}>
                            {vendor.name}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: '1rem 0.75rem', textAlign: 'right', fontSize: '0.875rem', fontWeight: '600', color: '#e2e8f0' }}>
                      ${vendor.revenue.toLocaleString()}
                    </td>
                    <td style={{ padding: '1rem 0.75rem', textAlign: 'right', fontSize: '0.875rem', color: '#6b7280' }}>
                      {vendor.reviews}
                    </td>
                    <td style={{ padding: '1rem 0.75rem', textAlign: 'right' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '0.25rem' }}>
                        <span style={{ color: '#f59e0b' }}>★</span>
                        <span style={{ fontSize: '0.875rem', color: '#e2e8f0' }}>{vendor.rating}</span>
                      </div>
                    </td>
                    <td style={{ padding: '1rem 0.75rem', textAlign: 'right' }}>
                      <span style={{ 
                        fontSize: '0.875rem', 
                        fontWeight: '600',
                        color: '#10b981'
                      }}>
                        +{vendor.growth}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {selectedTab === 'insights' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          {analyticsData.insights.map(insight => (
            <div key={insight.id} style={{ 
              backgroundColor: '#2d3748', 
              padding: '2rem', 
              borderRadius: '0.75rem', 
              boxShadow: '0 4px 6px rgba(0,0,0,0.3)',
              borderLeft: `6px solid ${getInsightColor(insight.type)}`
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <span style={{ fontSize: '2rem' }}>{getInsightIcon(insight.type)}</span>
                  <div>
                    <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#e2e8f0', margin: '0 0 0.25rem 0' }}>
                      {insight.title}
                    </h3>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                      <span style={{ 
                        fontSize: '0.75rem', 
                        padding: '0.25rem 0.5rem',
                        backgroundColor: getInsightColor(insight.type) + '20',
                        color: getInsightColor(insight.type),
                        borderRadius: '0.25rem',
                        textTransform: 'uppercase',
                        fontWeight: '600'
                      }}>
                        {insight.type}
                      </span>
                      <span style={{ 
                        fontSize: '0.75rem', 
                        padding: '0.25rem 0.5rem',
                        backgroundColor: insight.impact === 'high' ? '#ef444420' : insight.impact === 'medium' ? '#f59e0b20' : '#10b98120',
                        color: insight.impact === 'high' ? '#ef4444' : insight.impact === 'medium' ? '#f59e0b' : '#10b981',
                        borderRadius: '0.25rem',
                        textTransform: 'uppercase',
                        fontWeight: '600'
                      }}>
                        {insight.impact} Impact
                      </span>
                    </div>
                  </div>
                </div>
                
                {insight.actionable && (
                  <button style={{
                    padding: '0.75rem 1.5rem',
                    backgroundColor: getInsightColor(insight.type),
                    color: 'white',
                    border: 'none',
                    borderRadius: '0.375rem',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    cursor: 'pointer'
                  }}>
                    Take Action
                  </button>
                )}
              </div>
              
              <p style={{ fontSize: '1rem', color: '#a0aec0', margin: '0', lineHeight: '1.6' }}>
                {insight.description}
              </p>
            </div>
          ))}
        </div>
      )}

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}